import React from 'react';
import { Button, DatePicker, Form } from 'antd';
import moment from 'moment';

import FirebaseSetup from '../../FirebaseSetup';

import SsnLast4Input from './SsnLast4Input';
import SignupShell from './SignupShell';

const firebaseSetup = new FirebaseSetup();

/**
 * What the payout account still needs from the person.
 *
 * Sign-up no longer asks for a date of birth or SSN: Stripe first needs them
 * when the connected account is created, which happens at bank setup or on the
 * first transfer. So the Payouts screen asks for them once, right before the
 * first bank account. SSN last 4 is keyed the way Stripe keys it — on a US
 * payout (USD money, or a US-resident client) — not on the profile country
 * alone.
 */
export function needsSsnLast4(client, accounts) {
  if (!client) return false;
  // Stripe sends ssn_last_4 only when the connected account's country is US, and
  // that country is the client's own when set (strike-payer stripe-account-helper:
  // `country || currency's country`). So: a US resident, or — with no country on
  // file — USD money. An Irish client with a USD balance is never asked.
  if (client.country) return client.country === 'US';
  return (accounts || []).some((a) => a && a.currency === 'USD');
}

export function payoutDetailsMissing(client, accounts) {
  if (!client) return { dob: false, idLast4: false, any: false };
  const dob = client.dob == null;
  const idLast4 = needsSsnLast4(client, accounts) && !client.idLast4;
  return { dob, idLast4, any: dob || idLast4 };
}

function toMoment(val) {
  if (!val) return undefined;
  if (moment.isMoment(val)) return val;
  if (typeof val?.toMillis === 'function') return moment(val.toMillis());
  return moment(val);
}

/**
 * One screen, at most two asks. Saves {dob, idLast4?} through `onSave`.
 */
export default function PayoutDetailsForm({ client, accounts, onSave, saving }) {
  const [form] = Form.useForm();
  const missing = payoutDetailsMissing(client, accounts);
  const askSsn = needsSsnLast4(client, accounts) && !client?.idLast4;

  const onFinish = (values) => {
    const out = {};
    if (values.dob) {
      const firebase = firebaseSetup.getFirebase();
      out.dob = firebase.firestore.Timestamp.fromDate(new Date(values.dob.valueOf()));
    }
    if (askSsn && values.idLast4) out.idLast4 = values.idLast4;
    if (onSave) onSave(out);
  };

  const isCompany = !!(client && client.isCompany);
  const dateLabel = isCompany ? 'Founded on' : 'Date of birth';
  const lead = askSsn
    ? `Before your first bank account, our payment partner needs your ${dateLabel.toLowerCase()} and the last four digits of your SSN.`
    : `Before your first bank account, our payment partner needs your ${dateLabel.toLowerCase()}.`;

  return (
    <SignupShell title="Before you get paid" lead={lead}>
      <Form
        form={form}
        layout="vertical"
        size="large"
        onFinish={onFinish}
        initialValues={{ dob: toMoment(client?.dob) }}
      >
        {missing.dob && (
          <Form.Item
            name="dob"
            label={<span style={{ fontSize: 16 }}>{dateLabel}</span>}
            rules={[
              { required: true, message: isCompany ? 'Enter the founding date' : 'Enter your date of birth' },
              {
                validator: (_, value) => {
                  if (!value || isCompany) return Promise.resolve();
                  const age = moment().diff(toMoment(value), 'years', true);
                  return age < 13 ? Promise.reject(new Error('You must be 13+')) : Promise.resolve();
                },
              },
            ]}
          >
            <DatePicker
              allowClear={false}
              defaultPickerValue={moment(631152000000)}
              format="DD/MM/YYYY"
              placeholder="DD/MM/YYYY"
              inputReadOnly={false}
              style={{ width: '100%', height: 52, fontSize: 16, borderRadius: 8 }}
            />
          </Form.Item>
        )}
        {askSsn && (
          <Form.Item
            name="idLast4"
            label={<span style={{ fontSize: 16 }}>Last four digits of your SSN</span>}
            extra={<span style={{ fontSize: 14 }}>Only the last four. We never ask for the full number.</span>}
            rules={[
              { required: true, message: 'Enter the last four digits of your SSN' },
              { pattern: /^\d{4}$/, message: 'SSN last 4 must be exactly four digits' },
            ]}
          >
            <SsnLast4Input />
          </Form.Item>
        )}
        <Form.Item style={{ marginTop: 8 }}>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={!!saving}
            style={{ height: 52, fontSize: 18, fontWeight: 600, borderRadius: 8 }}
          >
            Continue to bank details
          </Button>
        </Form.Item>
      </Form>
    </SignupShell>
  );
}
